import { useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, Loader2, Minus, Plus, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { availableModifierGroups, reachableSelections, toggleModifier } from './modifiers'
import { posOrderingError, quotePosOrder } from './service'
import type { BasketItem, MenuItemDetail, ModifierGroup, PosOrderRequest, VariationSelection } from './types'

export const MAX_QUANTITY = 100
export const LINE_NOTE_MAX = 1000

type Translate = ReturnType<typeof useTranslation>['t']

export function formatMoney(language: string, value: number, currency = 'EUR') {
  try {
    return new Intl.NumberFormat(language, { style: 'currency', currency }).format(value)
  } catch {
    return `${value.toFixed(2)} ${currency}`
  }
}

export function posErrorText(t: Translate, code: string) {
  return t(`errors.${code}`, { defaultValue: t('errors.default') })
}

/** Empty strings intentionally fall back, unlike `??`. */
export function textOr(value: string | null | undefined, fallback: string) {
  return value !== undefined && value !== null && value !== '' ? value : fallback
}

export function clampQuantity(value: number) {
  if (!Number.isFinite(value)) return 1
  return Math.min(MAX_QUANTITY, Math.max(1, Math.round(value)))
}

const ROVING_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']
/** Arrow/Home/End navigation inside a radiogroup/tablist; items carry `data-roving`. */
export function rovingKeyDown(event: KeyboardEvent<HTMLElement>, activate: boolean) {
  if (!ROVING_KEYS.includes(event.key)) return
  const container = event.currentTarget.closest('[role="radiogroup"],[role="tablist"]')
  if (!container) return
  const items = Array.from(container.querySelectorAll<HTMLElement>('[data-roving]:not(:disabled)'))
  if (items.length === 0) return
  event.preventDefault()
  const rtl = getComputedStyle(container).direction === 'rtl'
  const index = items.indexOf(event.currentTarget)
  const forward = event.key === 'ArrowDown' || event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')
  const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
    : (index + (forward ? 1 : -1) + items.length) % items.length
  items[next].focus()
  if (activate) items[next].click()
}

const signed = (formatted: string, value: number) => (value > 0 ? `+ ${formatted}` : formatted)

/** Existing lines may use the legacy `variations` list; map each id onto the group that offers it. */
function initialSelections(item: MenuItemDetail, initial?: BasketItem): VariationSelection[] {
  if (!initial) return []
  if (initial.variationSelections?.length) return initial.variationSelections.map(s => ({ ...s }))
  return (initial.variations ?? []).map(id => {
    const group = item.modifierGroups.find(g => g.variations.some(v => v.id === id))
    return group ? { variation: id, slotKey: group.slotKey, position: group.slotPosition } : { variation: id, slotKey: null, position: null }
  })
}

const slotOf = (s: VariationSelection) => s.slotKey ?? ''

export interface ItemConfigurationProps {
  restaurantId: number
  item: MenuItemDetail
  context: { withDelivery: boolean; dineIn: boolean; dineInMenu: boolean }
  initial?: BasketItem
  initialNotes?: string
  editing: boolean
  disabled?: boolean
  onApply: (item: BasketItem, notes: string) => void
  onClose: () => void
}

export function ItemConfiguration({ restaurantId, item, context, initial, initialNotes, editing, disabled = false, onApply, onClose }: ItemConfigurationProps) {
  const { t, i18n } = useTranslation('posOrdering')
  const [{ start, removed }] = useState(() => {
    const all = initialSelections(item, initial)
    const valid = reachableSelections(item, all)
    return { start: valid, removed: all.filter(s => !valid.includes(s)) }
  })
  const [selections, setSelections] = useState<VariationSelection[]>(start)
  const [quantity, setQuantity] = useState(() => clampQuantity(initial?.quantity ?? 1))
  const [notes, setNotes] = useState(initialNotes ?? '')

  const line: BasketItem = {
    menuItem: item.id, quantity, variationSelections: selections,
    ...(initial?.extraItems ? { extraItems: initial.extraItems } : {}),
  }
  const request: PosOrderRequest = { shoppingItems: [line], ...context }
  const quote = useQuery({
    queryKey: ['pos-line-quote', restaurantId, JSON.stringify(request)],
    queryFn: ({ signal }) => quotePosOrder(restaurantId, request, signal),
    retry: false, staleTime: 0, refetchOnWindowFocus: false,
  })
  const quoted = quote.isSuccess && !quote.isFetching ? quote.data.items[0] : undefined
  const currency = quote.data?.currency ?? 'EUR'
  const money = (value: number) => formatMoney(i18n.language, value, currency)
  const problem = quote.isError ? posOrderingError(quote.error) : null

  // Backend treats only maximumVariationCount > 0 as a limit.
  const normalized = (group: ModifierGroup): ModifierGroup => ({
    ...group, maximumVariationCount: group.maximumVariationCount && group.maximumVariationCount > 0 ? group.maximumVariationCount : null,
  })
  const groups = availableModifierGroups(item, selections)
    .map(normalized)
    .sort((a, b) => a.slotPosition - b.slotPosition || a.position - b.position)
  const appearances = (group: ModifierGroup) => groups.filter(g => g.id === group.id)
  const optionName = (id: number) =>
    item.modifierGroups.flatMap(g => g.variations).find(v => v.id === id)?.name ?? t('config.unknownOption')

  const isSelected = (group: ModifierGroup, id: number) =>
    selections.some(s => s.variation === id && slotOf(s) === group.slotKey)
  const countIn = (group: ModifierGroup) =>
    selections.filter(s => slotOf(s) === group.slotKey && group.variations.some(v => v.id === s.variation)).length

  function choose(group: ModifierGroup, id: number) {
    if (disabled) return
    if (!group.multipleSelectionPossible && group.mandatory && isSelected(group, id)) return
    setSelections(current => toggleModifier(item, current, group, id))
  }

  function priceLabel(group: ModifierGroup, id: number, menuPrice: number) {
    if (isSelected(group, id)) {
      const price = quoted?.variations.find(v => v.variation === id && v.slotKey === group.slotKey)?.price
      if (price === undefined) return null
      return price === 0 ? t('config.included') : signed(money(price), price)
    }
    // Menu prices are indicative only; price rules may override them.
    if (item.priceRules.length === 0 && menuPrice !== 0) return signed(money(menuPrice), menuPrice)
    return null
  }

  const canApply = Boolean(quoted) && !disabled
  const apply = () => { if (canApply) onApply(line, notes.trim()) }

  return (
    <>
      <DialogHeader className="border-b px-5 pb-4 pt-5 text-start sm:px-6">
        <div className="flex items-start gap-4 pe-8">
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display text-2xl font-bold leading-tight">{item.name}</DialogTitle>
            <DialogDescription className="mt-1">{editing ? t('config.editLine') : t('config.newLine')}</DialogDescription>
          </div>
          <div className="shrink-0 text-end text-xl font-bold tabular-nums" aria-live="polite">
            {quoted ? money(quoted.total) : !problem && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label={t('config.pricePending')} />}
          </div>
        </div>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 sm:px-6">
        {item.description && <p className="pt-4 text-sm text-muted-foreground">{item.description}</p>}
        {item.allergens && <p className="pt-2 text-xs text-muted-foreground">{t('config.allergens', { list: item.allergens })}</p>}

        {removed.length > 0 && (
          <div role="alert" className="mt-4 flex gap-3 rounded-lg border border-status-warning/40 bg-status-warning/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning" />
            <div>
              <p className="font-semibold">{t('config.removedTitle')}</p>
              <p>{t('config.removedHint')}</p>
              <ul className="mt-1 list-disc ps-5">
                {removed.map((s, i) => <li key={`${s.variation}-${slotOf(s)}-${i}`}>{optionName(s.variation)}</li>)}
              </ul>
            </div>
          </div>
        )}

        {groups.map(group => {
          const multi = group.multipleSelectionPossible
          const count = countIn(group)
          const max = multi ? group.maximumVariationCount ?? null : 1
          const atMax = multi && max !== null && count >= max
          const same = appearances(group)
          const slotNumber = same.length > 1 ? same.indexOf(group) + 1 : null
          const headingId = `pos-group-${group.id}-${group.slotKey || 'base'}`
          const focusId = multi ? null
            : (group.variations.find(v => isSelected(group, v.id)) ?? (group.variations.length > 0 ? group.variations[0] : undefined))?.id ?? null
          return (
            <section key={`${group.id}-${group.slotKey}`} className="border-t py-4 first-of-type:border-t-0">
              <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 id={headingId} className="font-display text-lg font-bold">
                  {textOr(group.displayName, group.name)}
                  {slotNumber !== null && <span className="ms-2 text-sm font-medium text-muted-foreground">{t('config.slot', { n: slotNumber })}</span>}
                </h3>
                <span className={cn('text-sm', group.mandatory && count === 0 ? 'font-semibold text-status-warning' : 'text-muted-foreground')}>
                  {group.mandatory ? (count === 0 ? t('config.requiredMissing') : t('config.required')) : t('config.optional')}
                </span>
                {multi && max !== null && (
                  <span className="text-sm text-muted-foreground">{t('config.maxCount', { count: max, selected: count })}</span>
                )}
              </div>
              <div role={multi ? 'group' : 'radiogroup'} aria-labelledby={headingId} className="grid gap-2 sm:grid-cols-2">
                {group.variations.map(v => {
                  const selected = isSelected(group, v.id)
                  const price = priceLabel(group, v.id, v.price)
                  return (
                    <button
                      key={v.id}
                      type="button"
                      role={multi ? 'checkbox' : 'radio'}
                      aria-checked={selected}
                      {...(multi ? {} : { 'data-roving': true, tabIndex: v.id === focusId ? 0 : -1, onKeyDown: (e: KeyboardEvent<HTMLElement>) => rovingKeyDown(e, false) })}
                      disabled={disabled || (!selected && atMax)}
                      onClick={() => choose(group, v.id)}
                      className={cn(
                        'flex min-h-12 w-full items-center gap-3 rounded-lg border px-4 py-2 text-start transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                        selected ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'border-border bg-card hover:bg-accent',
                      )}
                    >
                      <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center border-2',
                        multi ? 'rounded' : 'rounded-full',
                        selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/50')}>
                        {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{v.name}</span>
                        {v.description && <span className="block text-xs text-muted-foreground">{v.description}</span>}
                      </span>
                      {price && <span className={cn('shrink-0 text-sm tabular-nums', !selected && 'text-muted-foreground')}>{price}</span>}
                    </button>
                  )
                })}
              </div>
            </section>
          )
        })}

        {groups.length > 0 && <p className="pb-2 text-xs text-muted-foreground">{t('config.priceHint')}</p>}

        <section className="border-t py-4">
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <Label htmlFor="pos-line-note" className="font-display text-lg font-bold">{t('config.noteLabel')}</Label>
            <span className="text-xs text-muted-foreground">{t('config.noteScope')}</span>
          </div>
          <Textarea id="pos-line-note" value={notes} maxLength={LINE_NOTE_MAX} rows={2} disabled={disabled}
            onChange={e => setNotes(e.target.value.slice(0, LINE_NOTE_MAX))} placeholder={t('config.notePlaceholder')} className="text-base" />
          <p className="mt-1 text-end text-xs tabular-nums text-muted-foreground">{t('fields.length', { count: notes.length, max: LINE_NOTE_MAX })}</p>
        </section>

        <section className="flex items-center justify-between gap-4 border-t py-4">
          <span id="pos-line-qty" className="font-display text-lg font-bold">{t('config.quantity')}</span>
          <div className="flex items-center" role="group" aria-labelledby="pos-line-qty">
            <Button type="button" variant="outline" size="icon-lg" aria-label={t('basket.decrease')} disabled={disabled || quantity <= 1}
              onClick={() => setQuantity(q => clampQuantity(q - 1))}><Minus /></Button>
            <span className="w-14 text-center text-lg font-semibold tabular-nums" aria-live="polite">{quantity}</span>
            <Button type="button" variant="outline" size="icon-lg" aria-label={t('basket.increase')} disabled={disabled || quantity >= MAX_QUANTITY}
              onClick={() => setQuantity(q => clampQuantity(q + 1))}><Plus /></Button>
          </div>
        </section>
      </div>

      <div className="border-t px-5 py-4 sm:px-6">
        {problem && !problem.cancelled && (
          <div role="alert" className="mb-3 flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p className="flex-1">{posErrorText(t, problem.code)}</p>
            {problem.code === 'request_failed' && (
              <Button type="button" variant="outline" size="sm" onClick={() => { void quote.refetch() }}>
                <RefreshCw />{t('actions.retry')}
              </Button>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" size="xl" onClick={onClose}>{t('actions.cancel')}</Button>
          <Button type="button" size="xl" disabled={!canApply} onClick={apply} className="sm:min-w-[16rem]">
            {quote.isFetching && <Loader2 className="animate-spin" />}
            {quoted
              ? (editing ? t('config.applyEdit', { total: money(quoted.total) }) : t('config.add', { total: money(quoted.total) }))
              : quote.isFetching ? t('config.pricePending') : (editing ? t('config.applyEditNoPrice') : t('config.addNoPrice'))}
          </Button>
        </DialogFooter>
      </div>
    </>
  )
}
